/**
 * Calendar (.ics) + Android alarm deep-link helpers for "remind me" actions.
 *
 * No backend, no native API needed:
 *  - `downloadIcs` builds a VEVENT and hands it to the OS via a `text/calendar`
 *    blob → the device's Calendar app offers "Add event". Works on iOS + Android.
 *  - `androidAlarmUrl` builds the public `android.intent.action.SET_ALARM`
 *    intent URL — opens the Clock app's set-alarm prefilled at the event's
 *    wall-clock time. **Android only** (iOS exposes no alarm API) and
 *    best-effort inside the Telegram WebView.
 */

interface CalendarEvent {
  title: string;
  /** ISO datetime string (e.g. tournament `started_at`). */
  start: string;
  /** Optional ISO end; defaults to start + 1h. */
  end?: string | null;
  description?: string;
}

/** Format a Date as an iCalendar UTC timestamp: `YYYYMMDDTHHMMSSZ`. */
function toIcsUtc(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** Escape per RFC 5545 text values (commas, semicolons, newlines). */
function escapeIcsText(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

/**
 * Resolve the event end: use `end` when it parses AND is after `start`;
 * otherwise default to start + 1h. Guards against bad/earlier `end_at` values
 * from the backend (which break calendar imports).
 */
function resolveEnd(start: Date, end?: string | null): Date {
  if (end) {
    const e = new Date(end);
    if (!Number.isNaN(e.getTime()) && e.getTime() > start.getTime()) return e;
  }
  return new Date(start.getTime() + 60 * 60 * 1000);
}

/**
 * Google Calendar "create event" template URL (https). Opening this externally
 * (`openLink`) pops the prefilled new-event sheet in Google Calendar (app or
 * web) on both iOS and Android — reliable inside the Telegram WebView, unlike a
 * `.ics` blob which the WebView just renders as text. Returns null on bad date.
 */
export function googleCalendarUrl(event: CalendarEvent): string | null {
  const start = new Date(event.start);
  if (Number.isNaN(start.getTime())) return null;
  const end = resolveEnd(start, event.end);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toIcsUtc(start)}/${toIcsUtc(end)}`,
    ...(event.description ? { details: event.description } : {}),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Build a single-event .ics document. Returns null if `start` is unparseable. */
export function buildIcs(event: CalendarEvent): string | null {
  const start = new Date(event.start);
  if (Number.isNaN(start.getTime())) return null;
  const dtEnd = resolveEnd(start, event.end);
  const uid = `${start.getTime()}-${Math.random().toString(36).slice(2)}@qlash.gg`;

  // CRLF line endings per RFC 5545.
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//QLASH//Telegram Mini App//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(dtEnd)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    ...(event.description ? [`DESCRIPTION:${escapeIcsText(event.description)}`] : []),
    'BEGIN:VALARM',
    'TRIGGER:-PT10M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeIcsText(event.title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

/**
 * Trigger the OS "add to calendar" sheet by downloading a `.ics` blob. Returns
 * false when the event can't be built (bad date). The browser/WebView hands the
 * `text/calendar` file to the Calendar app on both iOS and Android.
 */
export function downloadIcs(event: CalendarEvent, filename = 'event.ics'): boolean {
  const ics = buildIcs(event);
  if (!ics) return false;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the navigation/handoff has the URL.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return true;
}

/**
 * Build an `android.intent.action.SET_ALARM` intent URL for the event's
 * wall-clock time (local HH:MM). Android only. Returns null on a bad date.
 * SET_ALARM sets a one-time alarm at the next occurrence of that time.
 */
export function androidAlarmUrl(event: { title: string; start: string }): string | null {
  const d = new Date(event.start);
  if (Number.isNaN(d.getTime())) return null;
  const hour = d.getHours();
  const minutes = d.getMinutes();
  const msg = encodeURIComponent(event.title).replace(/'/g, '%27');
  return (
    'intent://qlash/alarm#Intent;scheme=qlash;action=android.intent.action.SET_ALARM;' +
    `i.android.intent.extra.alarm.HOUR=${hour};` +
    `i.android.intent.extra.alarm.MINUTES=${minutes};` +
    `S.android.intent.extra.alarm.MESSAGE=${msg};` +
    'B.android.intent.extra.alarm.SKIP_UI=false;' +
    'end'
  );
}

/**
 * Open the Android Clock set-alarm intent via a hidden-anchor click (lets the
 * WebView hand the `intent://` off to the system intent resolver — more
 * reliable than Telegram's http-only `openLink`). Android only, best-effort.
 * Returns false on a bad date.
 */
export function openAndroidAlarm(event: { title: string; start: string }): boolean {
  const url = androidAlarmUrl(event);
  if (!url) return false;
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}
